<template>
  <div>
    <v-container fluid fill-width class="ma-0 pa-0">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card color="#eeeeee" class="pt-1">
            <v-card-title class="primary--text pl-3 py-0"
              ><v-icon x-large class="pr-2" color="primary">analytics</v-icon>
              {{ title }}

              <v-divider vertical class="ml-4 mr-4"> </v-divider>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <v-chip
                      label
                      color="primary"
                      class="white--text title mr-2"
                      >{{ analysis.cohorts.length }}</v-chip
                    >
                    <span class="black--text text-body-1"
                      >Study group{{
                        analysis.cohorts.length == 1 ? '' : 's'
                      }}</span
                    >
                  </span>
                </template>
                <span class="subtitle-1">
                  {{
                    analysis.cohorts
                      .map(c => c.label.replace(/^\[O\]_/, ''))
                      .join(', ')
                  }}
                </span>
              </v-tooltip>

              <v-divider vertical class="ml-4 mr-4"> </v-divider>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <v-chip
                      label
                      color="primary"
                      class="white--text title mr-2"
                      >{{ analysis.outcomeVariables.length }}</v-chip
                    >
                    <span class="black--text text-body-1"
                      >Variable{{
                        analysis.outcomeVariables.length == 1 ? '' : 's'
                      }}</span
                    >
                  </span>
                </template>
                <span class="subtitle-1">
                  {{
                    analysis.outcomeVariables
                      .map(ov => ov.abbreviation)
                      .join(', ')
                  }}
                </span>
              </v-tooltip>

              <v-divider vertical class="ml-4 mr-4"> </v-divider>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <span class="black--text text-body-1">Analysis Type: </span>
                    <v-chip
                      label
                      color="primary"
                      class="white--text title mr-2"
                      >{{ selectedAnalysisMode }}</v-chip
                    >
                  </span>
                </template>
                <span class="subtitle-1">
                  {{ selectedAnalysisMode }} Mode
                </span>
              </v-tooltip>

              <v-divider
                v-if="analysis.collection.is_longitudinal"
                vertical
                class="ml-4 mr-4"
              >
              </v-divider>

              <v-tooltip
                v-if="analysis.collection.is_longitudinal"
                bottom
                color="primary"
              >
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <span class="black--text text-body-1">Comparing: </span>
                    <v-chip
                      label
                      color="primary"
                      class="white--text title mr-2"
                      >{{ analysis.input.comparisonFieldDescr }}</v-chip
                    >
                  </span>
                </template>
                <span class="subtitle-1">
                  Comparing {{ analysis.input.comparisonFieldLongDescr }}
                </span>
              </v-tooltip>

              <v-divider vertical class="ml-4 mr-4"> </v-divider>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <span class="black--text text-body-1">Status: </span>
                    <v-chip
                      label
                      :color="statusColor[analysis.status]"
                      class="white--text title mr-2"
                      >{{ analysis.status }}</v-chip
                    >
                  </span>
                </template>
                <span v-if="analysis.status == 'Failed'" class="subtitle-1">
                  {{ analysis.error }}
                </span>
                <span v-else class="subtitle-1">
                  {{ analysis.status }} at
                  {{ analysis.statusTime | formatDate }}
                </span>
              </v-tooltip>

              <v-spacer />

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <v-switch
                      v-model="syncAnalyses"
                      label="Sync selections"
                      class="pa-0 ma-0"
                      hide-details
                    ></v-switch>
                  </span>
                </template>
                <span>
                  Selections made in this analysis will be reflected in the
                  others.
                </span>
              </v-tooltip>
            </v-card-title>

            <div
              style="display:flex; align-items: center; justify-content: space-between;"
            >
              <v-card-subtitle class="primary--text pl-5 py-1 text-h6">
                {{ getAnalysisDescription() }}
              </v-card-subtitle>

              <!-- Three independent icons placed on the right -->
              <div style="height:100%; display: flex;">
                <v-row justify="end" style="width:200px" class="mr-4">
                  <v-tooltip v-if="analysis.index > 0" bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-icon
                          color="primary"
                          class="mx-1"
                          dark
                          medium
                          :disabled="cohortsMissing || outOfDate"
                          @click="
                            fullscreen = true;
                            resize();
                          "
                          >mdi-arrow-expand-all</v-icon
                        >
                      </span>
                    </template>
                    <span class="subtitle-1">
                      Show fullscreen flexible summary view.
                    </span>
                  </v-tooltip>

                  <v-tooltip v-if="analysis.index > 0" bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-icon
                          color="primary"
                          class="mx-1"
                          dark
                          medium
                          :disabled="cohortsMissing || outOfDate"
                          @click="classic_fullscreen = true"
                          >mdi-numeric-4-box-outline</v-icon
                        >
                      </span>
                    </template>
                    <span class="subtitle-1">
                      Show 4-panel fullscreen summary view.
                    </span>
                  </v-tooltip>

                  <v-dialog v-model="dialog" persistent max-width="500">
                    <template v-slot:activator="{ on, attrs }">
                      <v-tooltip
                        v-if="analysis.index > 0"
                        bottom
                        color="primary"
                      >
                        <template v-slot:activator="{ on: tooltip }">
                          <span v-on="{ ...tooltip }">
                            <v-icon
                              color="primary"
                              dark
                              v-bind="attrs"
                              class="mx-4"
                              medium
                              v-on="on"
                              >delete</v-icon
                            >
                          </span>
                        </template>
                        <span class="subtitle-1"> Delete analysis. </span>
                      </v-tooltip>
                    </template>
                    <v-card>
                      <v-card-title color="white" primary-title
                        >Delete Analysis</v-card-title
                      >

                      <v-card-text
                        class="primary primary--text text--lighten-5 pt-4"
                      >
                        Are you sure you want to delete ANALYSIS
                        {{ analysis.index }}?<br />
                        This action cannot be undone although the analysis can
                        be re-run (manually) if needed.
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="dialog = false">
                          <v-icon left>close</v-icon> Cancel
                        </v-btn>
                        <v-btn color="error" @click="deleteAnalysis"
                          ><v-icon left>delete</v-icon> Delete Analysis</v-btn
                        >
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-icon v-if="expanded" medium @click="expanded = false"
                          >expand_less</v-icon
                        >
                        <v-icon v-else medium @click="expanded = true"
                          >expand_more</v-icon
                        >
                      </span>
                    </template>
                    <span v-if="expanded" class="subtitle-1">
                      Minimize analysis.
                    </span>
                    <span v-else class="subtitle-1"> Expand analysis. </span>
                  </v-tooltip>
                </v-row>
              </div>
            </div>

            <v-card-subtitle
              v-if="analysis.status == 'Refreshing'"
              class="primary--text pl-5 py-3 text-h6"
            >
              {{ getAnalysisDescription() }}
            </v-card-subtitle>

            <v-card-subtitle
              v-else-if="noCohortsLeft"
              class="pa-1 mt-1 text-h6 yellow lighten-4 font-weight-regular black--text"
            >
              <v-icon class="pa-1" color="warning" medium>warning</v-icon>
              WARNING: All ({{ numOriginalCohorts }}) of the original selected
              study groups/predictors in this analysis have been deleted.
              <v-btn color="primary" small class="ml-4" @click="dialog = true"
                >Remove Analysis</v-btn
              >
            </v-card-subtitle>

            <v-card-subtitle
              v-else-if="oneCohortLeft"
              class="pa-1 mt-1 text-h6 yellow lighten-4 font-weight-regular black--text"
            >
              <v-icon class="pa-1" color="warning" medium>warning</v-icon>
              WARNING: All but one of the original selected study
              groups/predictors in this analysis have been deleted.
              <v-btn color="primary" small class="ml-4" @click="dialog = true"
                >Remove Analysis</v-btn
              >
            </v-card-subtitle>

            <v-card-subtitle
              v-else-if="cohortsMissing"
              class="pa-1 mt-1 text-h6 yellow lighten-4 font-weight-regular black--text"
            >
              <v-icon class="pa-1" color="warning" medium>warning</v-icon>
              WARNING: This analysis is out of date; one or more of the
              {{ numOriginalCohorts }} selected study groups/predictors have
              been deleted.
              <v-btn color="primary" small class="ml-4" @click="refresh()"
                >Refresh Analysis</v-btn
              >
              <v-btn color="primary" small class="ml-4" @click="dialog = true"
                >Remove Analysis</v-btn
              >
            </v-card-subtitle>

            <v-card-subtitle
              v-else-if="outOfDate"
              class="pa-1 mt-1 text-h6 yellow lighten-4 font-weight-regular black--text"
            >
              <v-icon class="pa-1" color="warning" medium>warning</v-icon>
              WARNING: This analysis is out of date; the study dataset has been
              modified since the analysis was run.
              <v-btn color="primary" small class="ml-4" @click="refresh()"
                >Refresh Analysis</v-btn
              >
              <v-btn color="primary" small class="ml-4" @click="dialog = true"
                >Remove Analysis</v-btn
              >
            </v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      v-model="classic_fullscreen"
      persistent
      fullscreen
      class="pa-0 ma-0"
    >
      <analysis-multi-panel-classic
        :id="'analysis-omp-' + analysis.index"
        :analysis="analysis"
        :title="'ANALYSIS ' + analysis.index"
        :multi-select="true"
        style="height: 100vh;"
        @deleteAnalysis="deleteAnalysis(analysis.index)"
        @cohortColorChange="updateCohortColor"
        @closeDialog="classic_fullscreen = false"
      />
    </v-dialog>

    <v-dialog
      v-if="fullscreen"
      v-model="fullscreen"
      persistent
      fullscreen
      class="pa-0 ma-0"
    >
      <analysis-multi-panel
        :id="'analysis-mp-' + analysis.index"
        :analysis="analysis"
        :title="'ANALYSIS ' + analysis.index"
        :multi-select="true"
        style="height: 100vh;"
        @deleteAnalysis="deleteAnalysis(analysis.index)"
        @cohortColorChange="updateCohortColor"
        @closeDialog="fullscreen = false"
      />
    </v-dialog>

    <v-sheet
      v-show="expanded"
      :id="'vs-' + analysis.index"
      color="white"
      class="rounded-lg shadow py-2"
    >
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <splitpanes
              ref="splitter"
              class="default-theme"
              @resize="splitPaneResized"
            >
              <pane size="45" min-size="15" max-size="60" class="white">
                <v-sheet color="white" height="100%">
                  <v-tabs v-model="leftTab" light>
                    <v-tab key="cohorts">
                      <span v-if="selectedAnalysisMode == 'Predictor based'"
                        >Study groups</span
                      >
                      <span v-else>Study groups</span>
                    </v-tab>
                    <v-tab key="analytics">
                      <span v-if="selectedAnalysisMode == 'Predictor based'"
                        >Outcome Variables/Analytics</span
                      >
                      <span v-else>Predictor Variables/Analytics</span>
                    </v-tab>
                    <v-tooltip top color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <v-tab key="table1" v-on="{ ...tooltip }"
                          >Sample Characteristics</v-tab
                        >
                      </template>
                      <span class="text-body-1"
                        >Sample Characteristics (Table 1)</span
                      >
                    </v-tooltip>

                    <v-tooltip top color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <v-tab key="table2" v-on="{ ...tooltip }"
                          >Summary of Results</v-tab
                        >
                      </template>
                      <span class="text-body-1"
                        >Summary of Results (Table 2)</span
                      >
                    </v-tooltip>

                    <v-tooltip v-if="false" top color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <v-tab key="table3" v-on="{ ...tooltip }"
                          >Pairwise Comparison</v-tab
                        >
                      </template>
                      <span class="text-body-1"
                        >Pairwise Comparison (Table 3)</span
                      >
                    </v-tooltip>

                    <v-tooltip v-if="false" top color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <v-tab key="table4" v-on="{ ...tooltip }"
                          >Correlations</v-tab
                        >
                      </template>
                      <span class="text-body-1">Correlations (Table 4)</span>
                    </v-tooltip>
                  </v-tabs>

                  <v-tabs-items v-model="leftTab" class="pt-3">
                    <v-tab-item key="cohorts" :eager="true">
                      <cohort-table
                        :cohorts="analysis.cohorts"
                        :select-cohorts="visibleCohorts"
                        :show-title-bar="false"
                        :expanded="expandAnalytics"
                        report-max-overlap
                        show-select
                        show-colors
                        @selectedCohorts="updateVisibleCohorts"
                        @cohortColorChange="updateCohortColor"
                      />
                    </v-tab-item>

                    <v-tab-item key="analytics" :eager="true">
                      <analytics-panel
                        ref="apanel"
                        :analysis="analysis"
                        :selected-variables="selectedVars"
                        :anova-pvals="analysis.pvals"
                        :anova-pvals-input="analysis.input"
                        :outcome-variables="analysis.outcomeVariables"
                        :show-category-icons="true"
                        :show-title-bar="false"
                        :multi-select="multiSelect"
                        autoselect-first-variable
                        :expanded="expandAnalytics"
                        color-scheme="brewer5"
                        @variablesSelected="variablesSelected"
                        @expandClicked="analyticsExpandClicked"
                      />
                    </v-tab-item>

                    <v-tab-item key="table1" :eager="true">
                      <summary-stats-table
                        title="Sample Characteristics"
                        :predictor-variables="analysis.predictorVariables"
                        :outcome-variables="analysis.outcomeVariables"
                        :selected-variables="selectedVars"
                        :cohorts="analysis.cohorts"
                        :show-category-icons="true"
                        :comparison-field="analysis.input.comparisonField"
                        :is-longitudinal="analysis.collection.is_longitudinal"
                        :analysis="analysis"
                        :selected-analysis-mode="selectedAnalysisMode"
                        display-mode="table1"
                        @variableSelected="variableSelected"
                      />
                    </v-tab-item>

                    <v-tab-item key="table2" :eager="true">
                      <summary-stats-table
                        title="Summary of Results"
                        :predictor-variables="analysis.predictorVariables"
                        :outcome-variables="analysis.outcomeVariables"
                        :selected-variables="selectedVars"
                        :cohorts="analysis.cohorts"
                        :visible-cohorts="visibleCohorts"
                        :show-category-icons="true"
                        :comparison-field="analysis.input.comparisonField"
                        :is-longitudinal="analysis.collection.is_longitudinal"
                        :analysis="analysis"
                        :anova-pvals="analysis.pvals"
                        :selected-analysis-mode="selectedAnalysisMode"
                        display-mode="table2"
                        @selectedCohorts="updateVisibleCohorts"
                        @variableSelected="variableSelected"
                      />
                    </v-tab-item>

                    <v-tab-item v-if="false" key="table3" :eager="true">
                      <div
                        column
                        align-center
                        justify-center
                        fill-width
                        class="py-2 pl-2"
                      >
                        <v-subheader
                          class="title primary--text text--lighten-3"
                        >
                          Not yet implemented.
                        </v-subheader>
                      </div>
                    </v-tab-item>

                    <v-tab-item v-if="false" key="table4" :eager="true">
                      <div
                        column
                        align-center
                        justify-center
                        fill-width
                        class="py-3 pl-2"
                      >
                        <v-subheader
                          class="title primary--text text--lighten-3"
                        >
                          Not yet implemented.
                        </v-subheader>
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </v-sheet>
              </pane>

              <pane size="65">
                <v-sheet color="white" height="100%" min-height="400px">
                  <v-tabs v-model="rightTab" light>
                    <v-tab key="boxplots">
                      <span>Boxplot/Bar graph</span>
                    </v-tab>
                    <v-tab
                      key="lview"
                      :disabled="
                        !analysis.collection.is_longitudinal ||
                          !selectedVarsContainsLongitudinal
                      "
                      >Longitudinal Graph</v-tab
                    >
                    <v-tab
                      v-if="!multiSelect && analysis.cohorts.length > 2"
                      key="pairwise"
                      >Pairwise Comparison</v-tab
                    >

                    <v-tab
                      v-if="
                        getCollectionHasPureToneAudiometryData(
                          analysis.collection
                        )
                      "
                      key="audiogram"
                      >Audiogram</v-tab
                    >

                    <v-tab v-if="selectedAnalysisMode == 'Outcome based'">
                      Regression Analysis
                    </v-tab>
                  </v-tabs>

                  <v-tabs-items v-model="rightTab">
                    <v-tab-item key="boxplots">
                      <v-container fluid fill-width class="ma-0 pa-0">
                        <v-row
                          v-for="(detailedView, dvn) in selectedVars"
                          :key="`bp-${analysis.index}-${dvn}`"
                          class="ma-0 pa-0"
                        >
                          <v-col cols="12" class="ma-0 pa-0 export-content">
                            <v-container
                              style="padding: 0; margin-top: 0.5rem; margin-bottom: 0.5rem;"
                            >
                              <v-row class="d-flex justify-center align-center">
                                <!-- title -->
                                <v-col
                                  cols="auto"
                                  class="text-center pb-0 pt-2"
                                >
                                  <v-container class="ma-0 pa-0">
                                    <v-row class="ma-0 pa-0">
                                      <v-col cols="12" class="ma-0 pa-0">
                                        <v-card-title
                                          class="primary--text pa-0 pl-2"
                                        >
                                          <v-tooltip bottom color="primary">
                                            <template
                                              v-slot:activator="{ on: tooltip }"
                                            >
                                              <img
                                                :src="
                                                  '/images/' +
                                                    detailedView.category +
                                                    '-icon-128.png'
                                                "
                                                :title="detailedView.category"
                                                style="height:1.25em"
                                                class="ma-1"
                                                @error="missingDomainFallback"
                                                v-on="{ ...tooltip }"
                                              />
                                            </template>
                                            <span>{{
                                              detailedView.category
                                            }}</span>
                                          </v-tooltip>

                                          <v-tooltip
                                            v-if="detailedView"
                                            top
                                            color="primary"
                                          >
                                            <template
                                              v-slot:activator="{ on: tooltip }"
                                            >
                                              <span
                                                class="text-subtitle-1"
                                                v-on="{ ...tooltip }"
                                              >
                                                {{
                                                  detailedView.abbreviation ===
                                                  detailedView.label
                                                    ? detailedView.abbreviation
                                                    : detailedView.abbreviation +
                                                      ' | ' +
                                                      detailedView.label
                                                }}
                                              </span>
                                            </template>
                                            <span
                                              >{{ detailedView.description }}
                                            </span>
                                          </v-tooltip>

                                          <span v-else> {{ title }} </span>
                                          <v-spacer />
                                        </v-card-title>
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-col>
                                <!-- export -->
                                <v-col
                                  cols="auto"
                                  class="text-center pb-0 pt-2"
                                >
                                  <export-interface
                                    :sec-level-parent="true"
                                    :analysis="analysis"
                                    :variable="selectedVars[0]"
                                    export-type="boxplot"
                                  />
                                </v-col>
                              </v-row>
                            </v-container>

                            <box-plots
                              v-if="
                                detailedView &&
                                  detailedView.data_category == 'Continuous'
                              "
                              ref="boxplots"
                              :detailed-view="detailedView"
                              :analysis="analysis"
                              :cohorts="visibleCohorts"
                              :show-title-bar="false"
                              :outcome-var="detailedView"
                              :max-cohorts="analysis.cohorts.length"
                              :selected-analysis-mode="selectedAnalysisMode"
                              :row-height="50"
                              :row-pad="12"
                              :bar-pad="5"
                            />
                            <stacked-bars
                              v-else
                              ref="stackedbars"
                              class="pt-3"
                              :analysis="analysis"
                              :cohorts="visibleCohorts"
                              :show-title-bar="false"
                              :outcome-var="detailedView"
                              :max-cohorts="analysis.cohorts.length"
                              :selected-analysis-mode="selectedAnalysisMode"
                              :row-height="50"
                              :row-pad="12"
                              :bar-pad="5"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>

                    <v-tab-item
                      key="lview"
                      :disabled="
                        !analysis.collection.is_longitudinal ||
                          !selectedVarsContainsLongitudinal
                      "
                      :eager="false"
                    >
                      <v-container fluid fill-width class="ma-0 pa-0">
                        <v-row
                          v-for="(detailedView, dvn) in selectedVars"
                          :key="`dv-${analysis.index}-${dvn}`"
                          class="ma-0 pa-0"
                        >
                          <v-col
                            v-if="
                              'is_longitudinal' in detailedView
                                ? detailedView.is_longitudinal
                                : false
                            "
                            cols="12"
                            class="ma-0 pa-0 export-content"
                          >
                            <v-container
                              style="padding: 0; margin-top: 0.5rem; margin-bottom: 0.5rem;"
                            >
                              <v-row class="d-flex justify-center align-center">
                                <!-- title -->
                                <v-col
                                  cols="auto"
                                  class="text-center pb-0 pt-2"
                                >
                                  <v-container class="ma-0 pa-0">
                                    <v-row class="ma-0 pa-0">
                                      <v-col cols="12" class="ma-0 pa-0">
                                        <v-card-title
                                          class="primary--text pa-0 pl-2"
                                        >
                                          <v-tooltip bottom color="primary">
                                            <template
                                              v-slot:activator="{ on: tooltip }"
                                            >
                                              <img
                                                :src="
                                                  '/images/' +
                                                    detailedView.category +
                                                    '-icon-128.png'
                                                "
                                                :title="detailedView.category"
                                                style="height:1.25em"
                                                class="ma-1"
                                                @error="missingDomainFallback"
                                                v-on="{ ...tooltip }"
                                              />
                                            </template>
                                            <span>{{
                                              detailedView.category
                                            }}</span>
                                          </v-tooltip>

                                          <v-tooltip
                                            v-if="detailedView"
                                            top
                                            color="primary"
                                          >
                                            <template
                                              v-slot:activator="{ on: tooltip }"
                                            >
                                              <span
                                                class="text-subtitle-1"
                                                v-on="{ ...tooltip }"
                                              >
                                                {{
                                                  detailedView.abbreviation ===
                                                  detailedView.label
                                                    ? detailedView.abbreviation
                                                    : detailedView.abbreviation +
                                                      ' | ' +
                                                      detailedView.label
                                                }}
                                              </span>
                                            </template>
                                            <span
                                              >{{ detailedView.description }}
                                            </span>
                                          </v-tooltip>

                                          <span v-else> {{ title }} </span>
                                          <v-spacer />
                                        </v-card-title>
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-col>
                                <!-- export -->
                                <v-col
                                  cols="auto"
                                  class="text-center pb-0 pt-2"
                                >
                                  <export-interface
                                    :analysis="analysis"
                                    :variable="selectedVars[0]"
                                    export-type="longitudinalGraph"
                                  />
                                </v-col>
                              </v-row>
                            </v-container>
                            <detailed-view
                              v-if="analysis.collection.is_longitudinal"
                              ref="dview"
                              :analysis="analysis"
                              :outcome-var="detailedView"
                              :cohorts="visibleCohorts"
                              :show-title-bar="false"
                              min-height="400px"
                              class="pt-3"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>

                    <v-tab-item
                      v-if="!multiSelect && analysis.cohorts.length > 2"
                      key="pairwise"
                      :eager="false"
                    >
                      <span class="text-caption"
                        >Please use full-screen mode for export feature</span
                      >
                      <tukey-hsd-heatmap
                        v-if="selectedVars.length == 1"
                        ref="tukeyhsd"
                        :analysis="analysis"
                        :cohorts="analysis.cohorts"
                        :visible-cohorts="visibleCohorts"
                        :outcome-var="selectedVars[0]"
                        :color-scheme="pairwiseColorScheme"
                        :show-title-bar="false"
                        :selected-analysis-mode="selectedAnalysisMode"
                        class="export-content"
                        @selectedCohorts="updateVisibleCohorts"
                      />
                    </v-tab-item>

                    <v-tab-item
                      v-if="
                        getCollectionHasPureToneAudiometryData(
                          analysis.collection
                        )
                      "
                      key="audiogram"
                      :eager="true"
                    >
                      <div
                        column
                        align-center
                        justify-center
                        fill-width
                        class="py-3 pl-2"
                      >
                        <audiogram
                          :analysis="analysis"
                          :visible-cohorts="visibleCohorts"
                          :show-high-freqs-default="true"
                        />
                      </div>
                    </v-tab-item>

                    <v-tab-item
                      v-if="selectedAnalysisMode == 'Outcome based'"
                      key="regression"
                      :eager="false"
                    >
                      <forest-plot
                        :analysis="analysis"
                        :visible-cohorts="visibleCohorts"
                        :selected-analysis-mode="selectedAnalysisMode"
                      ></forest-plot>
                    </v-tab-item>
                  </v-tabs-items>
                </v-sheet>
              </pane>
            </splitpanes>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>
  </div>
</template>

<script>
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

import AnalysisMultiPanel from '@/components/DataExplorer/AnalysisMultiPanel.vue';
import AnalysisMultiPanelClassic from '@/components/DataExplorer/AnalysisMultiPanelClassic.vue';
import SummaryStatsTable from '@/components/AnalysisSummary/SummaryStatsTable.vue';
import CohortTable from '@/components/common/CohortTable.vue';
import AnalyticsPanel from '@/components/DataExplorer/AnalyticsPanel.vue';
import DetailedView from '@/components/DataExplorer/DetailedView.vue';
import Audiogram from '@/components/DataExplorer/Audiogram.vue';
import BoxPlots from '@/components/AnalysisSummary/BoxPlots.vue';
import StackedBars from '@/components/AnalysisSummary/StackedBars.vue';
import TukeyHsdHeatmap from '@/components/AnalysisSummary/TukeyHSDHeatmap.vue';
import ForestPlot from '@/components/AnalysisSummary/ForestPlot.vue';
import logEvent from '@/utils/logging';
import ExportInterface from '@/components/common/ExportInterface.vue';
import { actions, state } from '@/store/modules/dataExplorer/types.js';
import { mapState, mapActions } from 'vuex';
import {
  getCollectionHasPureToneAudiometryData,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    AnalysisMultiPanel,
    AnalysisMultiPanelClassic,
    AnalyticsPanel,
    Audiogram,
    BoxPlots,
    CohortTable,
    DetailedView,
    ExportInterface,
    ForestPlot,
    Pane,
    StackedBars,
    Splitpanes,
    SummaryStatsTable,
    TukeyHsdHeatmap,
  },
  filters: {
    formatDate(ts) {
      // note that toISOString is going to give us UTC
      return new Date(ts);
    },
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Study Groups',
    },
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    multiSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      fullscreen: false,
      classic_fullscreen: false,
      expanded: true,
      dialog: false,
      expandAnalytics: true,
      selectedVars: [],
      leftTab: 1,
      rightTab: 0,
      visibleCohorts: [],
      status: 'Submitted',
      error: null,
      statusTime: null,
      statusColor: {
        Submitted: 'primary',
        Running: 'secondary',
        Refreshing: 'secondary',
        Completed: 'success',
        Failed: 'error',
      },
      pairwiseColorScheme: 'brewer5',
      syncAnalyses: false,
      getCollectionHasPureToneAudiometryData: getCollectionHasPureToneAudiometryData,
      selectedVarsContainsLongitudinal: false,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      screenResize: state.SCREEN_RESIZE,
    }),
    selectedAnalysisMode() {
      // note - don't read from state. infer from cohorts in analysis to display UI changes
      if (
        this.analysis.cohorts.length > 0 &&
        this.analysis.cohorts[0].label.startsWith('[O]_')
      ) {
        return 'Outcome based';
      } else {
        return 'Predictor based';
      }
    },
    outOfDate() {
      let generated_epoch = new Date(this.analysis.statusTime).getTime() * 1.0;
      return (
        this.analysis.status == 'Completed' &&
        generated_epoch < this.analysis.collection.last_modified_epoch
      );
    },
    numOriginalCohorts() {
      return this.analysis.input.cohort_ids.split(',').length;
    },
    cohortsMissing() {
      let n_cohorts_orig = this.numOriginalCohorts;
      let n_cohorts_now = this.analysis.cohorts.length;
      return n_cohorts_orig > n_cohorts_now;
    },
    noCohortsLeft() {
      return this.analysis.cohorts.length == 0;
    },
    oneCohortLeft() {
      return this.analysis.cohorts.length == 1;
    },
  },
  watch: {
    analysis(a) {
      if (a != null) {
        this.visibleCohorts = this.analysis.cohorts;
      }
    },
    'analysis.cohorts': function() {
      // propagate change to child components
      if (this.visibleCohorts.length > 0) {
        this.visibleCohorts.splice(0, 1, this.visibleCohorts[0]);
      }
    },
    leftTab(lt) {
      logEvent(
        this.$gtag,
        null,
        null,
        'left_tab_changeto_' + lt,
        'analysis',
        'analysis ' + this.analysis.index + ' '
      );
      if (this.syncAnalyses) this.$emit('leftTab', lt);
    },
    rightTab(rt) {
      // workaround for resize problem #596
      if (rt == 1) {
        const dv = this.$refs.dview;
        setTimeout(async function() {
          if (dv) dv.map(c => c.onResize());
        }, 500);
      } else if (rt == 0) {
        const bp = this.$refs.boxplots;
        const sb = this.$refs.stackedbars;
        setTimeout(async function() {
          if (bp) bp.map(c => c.onResize());
          if (sb) sb.map(c => c.onResize());
        }, 500);
      }
      window.dispatchEvent(new Event('resize'));

      logEvent(
        this.$gtag,
        null,
        null,
        'right_tab_changeto_' + rt,
        'analysis',
        'analysis ' + this.analysis.index + ' '
      );
      if (this.syncAnalyses) this.$emit('rightTab', rt);
    },
    expanded(ex) {
      if (this.syncAnalyses) this.$emit('expanded', ex);
    },
    syncAnalyses(sa) {
      this.$emit('syncAnalyses', sa);
    },
    selectedVars(sv) {
      if (sv.length > 0) {
        this.selectedVarsContainsLongitudinal = sv.some(i =>
          'is_longitudinal' in i ? i.is_longitudinal : false
        );
      } else {
        this.selectedVarsContainsLongitudinal = false;
      }
    },
    selectedVarsContainsLongitudinal(val) {
      // forces user out of longitudinal tab is variable is not longitudinal
      if (!val && this.rightTab == 1) {
        this.setRightTab(0);
      }
    },
  },
  created() {
    if (this.analysis != null) {
      this.visibleCohorts = this.analysis.cohorts;
    }
  },
  methods: {
    ...mapActions('dataExplorer', {
      setScreenResize: actions.SET_SCREEN_RESIZE,
    }),
    resize() {
      this.setScreenResize(!this.screenResize);
    },
    variableSelected(sv) {
      if (this.selectedAnalysisMode == 'Predictor based') {
        if ('flip_axis' in sv) {
          this.selectedVars = [sv];
        }
      } else {
        this.selectedVars = [sv];
      }
    },
    variablesSelected(sv) {
      if (this.selectedAnalysisMode == 'Predictor based') {
        this.selectedVars = sv.filter(v => 'flip_axis' in v);
      } else {
        this.selectedVars = sv;
      }
      if (this.syncAnalyses) this.$emit('variablesSelected', sv);
    },
    analyticsExpandClicked(nv) {
      this.expandAnalytics = nv;
    },
    updateVisibleCohorts(vc) {
      // keep visibleCohorts sorted in the same order as cohorts
      var vcids = {};
      vc.forEach(c => {
        vcids[c.id] = true;
      });
      this.visibleCohorts = this.analysis.cohorts.filter(c => vcids[c.id]);
      if (this.syncAnalyses) this.$emit('cohortsSelected', this.visibleCohorts);
    },
    deleteAnalysis() {
      this.$emit('deleteAnalysis');
      this.dialog = false;
    },
    getComparisonFieldDescription(cfield) {
      const descrs = {
        change: 'Change from first visit - last visit',
        firstVisit: 'First visit',
        lastVisit: 'Last visit',
      };
      return descrs[cfield];
    },
    splitPaneResized(evt) {
      var psize = evt[0].size;
      this.expandAnalytics = psize > 30;
      if (this.rightTab == 1) {
        this.$nextTick(() => {
          if (this.$refs.dview) this.$refs.dview.map(c => c.onResize());
        });
      } else if (this.rightTab == 0) {
        this.$nextTick(() => {
          if (this.$refs.boxplots) this.$refs.boxplots.map(c => c.onResize());
          if (this.$refs.stackedbars)
            this.$refs.stackedbars.map(c => c.onResize());
        });
      }
      window.dispatchEvent(new Event('resize'));
    },
    updateCohortColor(cc) {
      // propagate event
      this.$emit('cohortColorChange', {
        cohort: cc['cohort'],
        color: cc['color'],
        pattern: cc['pattern'],
      });
    },
    getAnalysisDescription() {
      let dl2descr = {
        left_y_axis: 'First visit',
        right_y_axis: 'Last visit',
        change: 'First - Last visit change',
      };

      // predictor variables used
      let predVars = {};
      this.analysis.cohorts.forEach(c => {
        const queries = c.queries;
        queries.forEach(q => {
          let dl_lbl = q.input_variable.dimension_label
            ? ' - ' + dl2descr[q.input_variable.dimension_label]
            : '';
          if ('subject_ontology' in q.input_variable) {
            predVars[q.input_variable.subject_ontology.label + dl_lbl] = 1;
          } else if ('observation_ontology' in q.input_variable) {
            predVars[q.input_variable.observation_ontology.label + dl_lbl] = 1;
          }
        });
      });

      if (this.selectedAnalysisMode == 'Predictor based') {
        return 'Predictors: ' + Object.keys(predVars).join(' and ');
      } else {
        return 'Outcomes: ' + Object.keys(predVars).join(' and ');
      }
    },
    setLeftTab(lt) {
      this.leftTab = lt;
    },
    setRightTab(rt) {
      this.rightTab = rt;
    },
    setSelectedVariables(sv) {
      // filter selected variables based on current analysis
      var selIds = {};
      sv.map(v => {
        selIds[v.id] = 1;
      });
      let fsv = this.analysis.outcomeVariables.filter(ov => ov.id in selIds);
      this.selectedVars = fsv;
      this.expanded = fsv.length > 0;
    },
    setVisibleCohorts(vc) {
      // filter cohorts based on current analysis
      var selIds = {};
      vc.map(v => {
        selIds[v.id] = 1;
      });
      let fvc = this.analysis.cohorts.filter(ov => ov.id in selIds);
      this.visibleCohorts = fvc;
      this.expanded = fvc.length > 0;
    },
    setExpanded(ex) {
      this.expanded = ex;
    },
    setSyncAnalyses(sa) {
      this.syncAnalyses = sa;
    },
    refresh() {
      this.$emit('refreshAnalysis');
    },
  },
};
</script>
<style>
table.v-data-table thead tr th {
  font-size: 24px;
}
table.v-data-table tbody tr td {
  font-size: 24px;
}
</style>
